import tippy from 'tippy.js'
import { keyCodes } from '../../../shared/constants/keyboard';
import { createAccount, removeErrors } from '../../api';
import { each } from '../../../shared/utils/dom';

// re-assign some API methods
const { getSecondaryLookupField } = createAccount;

/**
 * Handle the JSON response of the lookup for getting secondary lookup field
 * @this Vue The Vue app instance
 * @param {object} response
 * @param {string} response.hasDateOfBirth
 * @param {string} response.hasTaxId
 * @param {string} response.hasLastFourSocial
 */
function secondaryLookupEmit(response) {
  this.$emit('account-lookup-response', response);
}

/**
 * Helper function to clear the fields and hide the verify fields
 * @param {object} model The Vue instance
 */
function clearVerifyFields(model) {
  model.$emit('clear-verify-fields');
  /* eslint-enable */
}

/**
 * Account lookup component
 */
Vue.component('account-lookup', {
  template: `
  <div class="form-field__wrapper">
    <label for="" id="account-number-label">
      Account Number
<span class="tooltip tooltip__container" id="tooltip-linkAccount-span" style="padding-top: 10px; display: inline; float: none;" data-tooltip-id="tooltip-linkAccount">
                    <span class="tooltip tooltip__trigger" data-tooltip-id="tooltip-linkAccount"><span class="a11y-hide">Click to expand link account tooltip</span></span>
                    <svg aria-hidden="true" class="icon icon-question tooltip__icon" role="img">
                        <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-question"></use>
                    </svg>
                </span>
            </h2>
            <div id="tooltip-linkAccount" class="tooltip-content">
                <button data-close="true">&times;</button>
                <span>Your account number is {{accountSize()}} digits long.</span>
            </div>
      <input id="account-number-input" type="text" name="account_number" class="w-100" :maxlength="accountNumberMaxLength"
        v-bind:value="value"
        v-on:input="$emit('input', $event.target.value)"
        v-on:keyup="keyupAccountNumber"
        v-on:paste="keyupAccountNumber"
        v-bind:placeholder="accountNumberPlaceholder"
      >
    </label>
    <button class="btn" disabled="disabled" type="button"
      v-bind:disabled="disabledAccountNumber"
      :class="showVerifyFields ? 'js-default-hidden' : ''"
      v-on:click="clickLookupByAccountNumber"
      id="search-by-account-number"
      >
      <span class="btn-inner">Search</span>
    </button>
    <button class="btn btn--gray js-default-hidden" type="button"
      :class="showVerifyFields ? 'js-show' : ''"
      v-on:click.prevent="clickClearAccountNumber">
      <span class="btn-inner">Clear</span>
    </button>
  </div>
  `,

  /**
   * Props array
   */
  props: {
    value: { required: true },
    ldc: { default: null },
    showVerifyFields: { type: Boolean },
    reproofing: { type: Boolean, default: false },
    mask: { default: true },
  },

  created() {
  },
  mounted() {
    each(document.querySelectorAll('.tooltip'), (el) => {
      tippy(el, {
        arrow: true,
        flip: true,
        html: `#${el.dataset.tooltipId}`,
        theme: 'nisource',
        trigger: 'click',
      });
    });
    if (window.location.href.indexOf("manage") < 0) {
      document.getElementById("tooltip-linkAccount-span").style.visibility = "hidden";
    }
  },

  /**
   * Data function
   */
  data() {
    return {
      disabledAccountNumber: true,
      loading: false,
    }
  },

  methods: {
    /**
     * Clear button click
     */
    clickClearAccountNumber() {
      clearVerifyFields(this);
      removeErrors();
      this.value = null;
      this.disabledAccountNumber = true;
    },

    clickLookupByAccountNumber() {
      if (this.unmaskedAccountNumber !== '') {
        // if you can click, the button is not disabled, and since we're only enabling the button
        // after validating the field value (see keyup$AccountNumber) then a click on the button
        // assumes it's already valid so we'll just lookup by field value
        this.$emit('component-loading', this.loading = true);
        this.noAccountsFound = false;
        Event.$emit('UpdateNoAccountsFoundFlag', { noAccountsFound: this.noAccountsFound });
        if (window.fabricator) {
          setTimeout(() => {
            secondaryLookupEmit.call(this, { dateOfBirthCount: 1 });
            this.$emit('component-loading', this.loading = false);
          }, 500);
        } else {
          getSecondaryLookupField({ accountNumber: this.unmaskedAccountNumber, ldc: this.ldc }, this.reproofing)
            .then(response => {
              this.$emit('account-lookup-response', response.data);
              this.showVerifyFields = true;
              this.$emit('component-loading', this.loading = false);
            })
            .catch(err => {
              if (err.response && err.response.status === 400) {
                this.$emit('component-loading', this.loading = false);
                this.noAccountsFound = true;
                Event.$emit('UpdateNoAccountsFoundFlag', { noAccountsFound: this.noAccountsFound });
              }
            });
        }
      }
    },

    /**
     * Validate the account number field on keyup
     */
    keyupAccountNumber(event) {
      // don't care about enter key
      if (event.keyCode === keyCodes.enter) {
        this.clickLookupByAccountNumber();
      }
    },

    accountSize() {
      if (this.ldc === 'NI' || this.ldc === 'BS') {
        return '10';
      }
      return '15';
    },
  },

  computed: {
    accountNumberPlaceholder() {
      // The placeholder for the account number changes based on LDC.
      if (this.ldc === 'NI' || this.ldc === 'BS') {
        return 'XXX - XXX - XXX - X';
      }
      return 'XXXXXXXX - XXX - XXXX';
    },
    unmaskedAccountNumber() {
      return this.value ? this.value.replace(/\s|-/g, "") : '';
    },
    accountNumberMaxLength() {
      if (this.ldc === 'NI' || this.ldc === 'BS') {
        return 13;
      }
      return 17;
    },
  },

  watch: {
    value(newValue) {
      let x;
      if (this.ldc === 'NI' || this.ldc === 'BS') {
        x = newValue.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,1})/);
        this.value = !x[2] ? x[1] : ''.concat(x[1], '-', x[2], (x[3] ? ''.concat('-', x[3]) : ''), (x[4] ? ''.concat('-', x[4]) : ''));
        this.disabledAccountNumber = !(this.unmaskedAccountNumber.length === 10);
      } else {
        x = newValue.replace(/\D/g, '').match(/(\d{0,8})(\d{0,3})(\d{0,4})/);
        this.value = !x[2] ? x[1] : ''.concat(x[1], '-', x[2], (x[3] ? ''.concat('-', x[3]) : ''), (x[4] ? ''.concat('-', x[4]) : ''));
        this.disabledAccountNumber = !(this.unmaskedAccountNumber.length === 15);
      }
    },
  },

});



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/vue/components/accountLookup.js